/** Popover-based multi-select for a small set of fixed options. */
import { useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { Checkbox } from '@/frontend/components/ui/checkbox'
import { inputStyles } from '@/frontend/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/frontend/components/ui/popover'
import { cn } from '@/frontend/lib/utils'

export function MultiSelectFilter({
  filterKey,
  options,
  selected,
  onChange,
  placeholder,
  searchable = false,
  allowClear = false,
  disabled = false,
}: {
  /** Unique key used to namespace checkbox IDs — prevents duplicate IDs when multiple instances render on the same page. */
  filterKey: string
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (values: string[]) => void
  placeholder: string
  searchable?: boolean
  /** Show a "Clear" button at the bottom of the popover when items are selected. */
  allowClear?: boolean
  /** When true, the trigger is non-interactive and styled like a disabled form control. */
  disabled?: boolean
}) {
  const [search, setSearch] = useState('')

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  const allOrNoneSelected = selected.length === 0 || selected.length === options.length
  const label = allOrNoneSelected
    ? placeholder
    : options
        .filter((o) => selected.includes(o.value))
        .map((o) => o.label)
        .join(', ')

  const visibleOptions = searchable
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options

  return (
    <Popover onOpenChange={(open) => { if (!open) setSearch('') }}>
      <PopoverTrigger
        disabled={disabled}
        className={cn(inputStyles, 'inline-flex h-8 w-36 items-center justify-between px-3')}
      >
        <span className={cn('truncate', allOrNoneSelected && 'text-muted-foreground')}>{label}</span>
        <ChevronDown aria-hidden="true" className="size-3 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        {searchable && (
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="mb-1 w-full border-0 border-b px-2 py-1.5 text-sm outline-none rounded-none"
          />
        )}
        <div className="max-h-72 overflow-y-auto">
          {visibleOptions.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">No matches</p>
          ) : (
            visibleOptions.map((o) => (
              <label
                key={o.value}
                htmlFor={`${filterKey}-filter-${o.value}`}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
              >
                <Checkbox
                  id={`${filterKey}-filter-${o.value}`}
                  checked={selected.includes(o.value)}
                  onCheckedChange={() => toggle(o.value)}
                />
                {o.label}
              </label>
            ))
          )}
        </div>
        {allowClear && selected.length > 0 && !allOrNoneSelected && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full border-t px-2 py-1.5 text-sm text-destructive-fg hover:text-destructive-fg/80"
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
